import { CompanySwitcher } from '@/features/companies/CompanySwitcher';
import { NotificationNavBadge } from '@/features/notifications/useNotificationFeed';
import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { motion } from 'motion/react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Button, Drawer, Skeleton, SkeletonRows, Tooltip } from '@/components/ui';

export function PageSkeleton() {
  return (
    <div role="status" aria-label="Loading page">
      <div className="page-header">
        <Skeleton width="30%" height={26} />
        <Skeleton width="45%" height={14} />
      </div>
      <SkeletonRows rows={6} />
    </div>
  );
}
import { cx } from '@/lib/cx';
import { brandingStyle } from '@/lib/branding';
import { useTheme } from '@/lib/theme';
import { useMediaQuery } from '@/lib/hooks';
import { ModuleGate, PlatformBanner } from '@/lib/platformAppearance';
import { useSession } from '@/lib/session';
import { visibleNav } from './nav';
import { AnnouncementBar } from './AnnouncementBar';
import { NotificationsBell } from './NotificationsBell';
import { UpdatesPanel } from './UpdatesPanel';
import { UserMenu } from './UserMenu';
import { AppHeader } from './AppHeader';

const COLLAPSE_KEY = 'cop.sidebar.collapsed';

function SidebarContent({ collapsed, onNavigate, showCollapse, onToggleCollapse }: { collapsed: boolean; onNavigate?: () => void; showCollapse: boolean; onToggleCollapse?: () => void }) {
  const { principal, impersonation } = useSession();
  const groups = visibleNav(principal);
  const name = principal?.branding?.displayName || principal?.tenantName || 'Workspace';
  return (
    <aside className="sidebar" aria-label="Primary">
      <div className="sidebar-head">
        <span className="workspace-mark" aria-hidden>
          {name.slice(0, 1).toUpperCase()}
        </span>
        <div className="workspace-name">
          <strong>{name}</strong>
          <span>{principal?.roleLabel}{impersonation ? " | Read-only" : ""}</span>
        </div>
      </div>
      <nav className="nav">
        {groups.map((g, gi) => (
          <div className="nav-group" key={g.label ?? gi} role="group" aria-label={g.label}>
            {g.label && <div className="nav-group-label">{g.label}</div>}
            {g.items.map((item) => {
              const Icon = item.icon;
              const link = (
                <NavLink key={item.to} to={item.to} end={item.end} aria-label={item.label} className="nav-link" onClick={onNavigate}>
                  {({ isActive }) => (
                    <>
                      {isActive && <motion.span layoutId="nav-active" className="nav-active-bg" transition={{ type: 'spring', stiffness: 500, damping: 40 }} />}
                      <Icon aria-hidden />
                      <span>{item.label}</span>{item.to.endsWith('/notifications') && <NotificationNavBadge />}
                    </>
                  )}
                </NavLink>
              );
              return collapsed ? (
                <Tooltip key={item.to} content={item.label} side="right">
                  {link}
                </Tooltip>
              ) : (
                link
              );
            })}
          </div>
        ))}
      </nav>
      <div className="sidebar-foot">
        {showCollapse && onToggleCollapse && (
          <Button variant="ghost" size="sm" className="collapse-btn" icon={collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />} onClick={onToggleCollapse} aria-expanded={!collapsed} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            Collapse
          </Button>
        )}
        <UserMenu variant="chip" />
      </div>
    </aside>
  );
}

export function StaffShell() {
  const { resolved } = useTheme();
  const location = useLocation();
  const desktop = useMediaQuery('(min-width: 1024px)');
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });
  };

  const { principal } = useSession();
  useEffect(() => {
    const tokens = brandingStyle(principal?.branding, resolved);
    if (!tokens) return;
    const root = document.documentElement;
    const previous = Object.keys(tokens).map(key => [key, root.style.getPropertyValue(key)] as const);
    for (const [key, value] of Object.entries(tokens)) root.style.setProperty(key, String(value));
    return () => { for (const [key, value] of previous) { if (value) root.style.setProperty(key, value); else root.style.removeProperty(key); } };
  }, [principal?.branding, resolved]);
  const title = principal?.branding?.displayName || principal?.tenantName;

  return (
    <div style={brandingStyle(principal?.branding, resolved)} className={cx('shell app-shell staff-shell', desktop && collapsed && 'is-collapsed')}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      {desktop && <SidebarContent collapsed={collapsed} showCollapse onToggleCollapse={toggle} />}
      <div className="main">
        <PlatformBanner />
        <AppHeader workspaceControl={<CompanySwitcher />} desktop={desktop} context={title ?? 'Workspace'} groups={visibleNav(principal)} onToggleNavigation={desktop ? toggle : () => setMobileOpen(true)} navigationExpanded={desktop ? !collapsed : mobileOpen} tools={<><UpdatesPanel /><NotificationsBell /></>} />
        <main id="main" tabIndex={-1} className="content">
          <AnnouncementBar />
          <motion.div key={location.pathname} className="route-fade" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}>
            <Suspense fallback={<PageSkeleton />}>
              <ModuleGate>
                <Outlet />
              </ModuleGate>
            </Suspense>
          </motion.div>
        </main>
      </div>
      {!desktop && (
        <div className="mobile-nav">
          <Drawer className="app-mobile-navigation" open={mobileOpen} onOpenChange={setMobileOpen} title="Navigation" side="left" size="sm">
            <SidebarContent collapsed={false} showCollapse={false} onNavigate={() => setMobileOpen(false)} />
          </Drawer>
        </div>
      )}
    </div>
  );
}
